import { create } from 'zustand';

export type PermissionCode =
  | 'VIEW_MATTERS'
  | 'VIEW_HEARINGS'
  | 'VIEW_CALENDAR'
  | 'COORDINATE_SCHEDULING'
  | 'MANAGE_TASKS'
  | 'VIEW_COMMUNICATIONS'
  | 'SEND_COMMUNICATIONS'
  | 'MANAGE_DOCUMENTS'
  | 'RESPOND_TO_ALLOCATIONS'
  | 'MANAGE_FEES';

export interface ClerkWorkspaceEntry {
  barristerId: string;
  barristerUserId?: string;
  barristerName: string;
  permissions: PermissionCode[];
  permissionsStatus: 'pending' | 'active';
}

interface ClerkWorkspaceState {
  workspaces: ClerkWorkspaceEntry[];
  activeBarristerId: string | null;
  isLoaded: boolean;
  setWorkspaces: (entries: ClerkWorkspaceEntry[]) => void;
  setActiveBarrister: (id: string) => void;
  hasPermission: (code: PermissionCode) => boolean;
  activeWorkspace: () => ClerkWorkspaceEntry | null;
}

export const useClerkWorkspaceStore = create<ClerkWorkspaceState>()((set, get) => ({
  workspaces: [],
  activeBarristerId: null,
  isLoaded: false,

  setWorkspaces: (entries) => set({ workspaces: entries, isLoaded: true }),

  setActiveBarrister: (id) => set({ activeBarristerId: id }),

  hasPermission: (code) => {
    const { workspaces, activeBarristerId, isLoaded } = get();
    // Optimistic while loading — don't flash the locked state
    if (!isLoaded) return true;
    if (workspaces.length === 0) return false;
    if (activeBarristerId) {
      const ws = workspaces.find((w) => w.barristerId === activeBarristerId);
      return ws ? ws.permissions.includes(code) : false;
    }
    // No specific context — union of all assigned barristers
    return workspaces.some((w) => w.permissions.includes(code));
  },

  activeWorkspace: () => {
    const { workspaces, activeBarristerId } = get();
    if (activeBarristerId) {
      return workspaces.find((w) => w.barristerId === activeBarristerId) ?? null;
    }
    return workspaces[0] ?? null;
  },
}));
